/** External steps (docs/plans/wizard-external-website-step.md): the wizard's
    server FUNCTIONS (Base tab), a `function` step (server-to-server: an
    outside API with a stored credential), and a `redirect` step (out to
    another website and back through a single-use callback). Every value here
    is read by the SERVER from the saved model — the browser never carries a
    URL, a credential or what the other site posted back.

    The code of a function lives at `src/wizard_models/<wizard>/fn__<name>.py`
    (`run(args)`, the business-logic sandbox, `api.credential()` /
    `api.http()` / `api.callback_url()` / `api.payload()` / `api.state()`);
    Ask-AI's business-logic coder writes it. Logic that only touches the
    database (PL/SQL, PL/pgSQL) is an ACTION of the base transaction, run by
    an Action step — not a function here. */

import { AnswerMapping } from './FetchEditor'
import { KeyValueEditor, ObjectPicker, RouteStringPicker } from './pickers'
import { useWizardDesign } from './context'
import type { WizardModel, WizardStep } from './types'
import type {
  WizardFunctionCall, WizardFunctionDecl, WizardPollBlock, WizardPollCondition, WizardRedirectBlock,
} from '@twasta/page-runtime/wizard/contract'
import {
  AddBtn, Field, Grid2, NumberInput, RemoveBtn, SelectInput, StringList, Sub, TextInput,
  emptyHintStyle, hintStyle,
} from './ui'

const NAME_RE = /^[a-z][a-z0-9_]{0,60}$/

/** Base tab: the wizard's declared functions. */
export function FunctionsEditor({ model, onChange }: { model: WizardModel; onChange: (m: WizardModel) => void }) {
  const ctx = useWizardDesign()
  const list: WizardFunctionDecl[] = model.functions || []
  const set = (next: WizardFunctionDecl[]) => onChange({ ...model, functions: next.length ? next : undefined })
  return (
    <div>
      {list.length === 0 && (
        <div style={emptyHintStyle}>
          None. A function is Python on the server — calls to an outside API with a stored credential,
          or reading what another website posted back.
        </div>
      )}
      {list.map((f, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
          <div style={{ flex: '0 0 30%' }}>
            <TextInput mono value={f.name} placeholder="gst_generate_irn" disabled={ctx.readOnly}
              onChange={(name) => set(list.map((x, j) => (j === i ? { ...x, name } : x)))} />
            {f.name && !NAME_RE.test(f.name) && (
              <div style={{ ...hintStyle, color: 'var(--color-error, #d33)' }}>lower_snake_case only</div>
            )}
            <div style={hintStyle}>
              src/wizard_models/{model.obj_name || '<wizard>'}/fn__{f.name || '<name>'}.py
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <TextInput value={f.description} placeholder="What it does" disabled={ctx.readOnly}
              onChange={(description) => set(list.map((x, j) => (j === i ? { ...x, description: description || undefined } : x)))} />
            <Field label="Hosts it may call (beside the credential's endpoint)">
              <StringList values={f.hosts || []} placeholder="api.example.com or *.example.com"
                onChange={(hosts) => set(list.map((x, j) => (j === i ? { ...x, hosts: hosts.length ? hosts : undefined } : x)))} />
            </Field>
          </div>
          <RemoveBtn onClick={() => set(list.filter((_, j) => j !== i))} />
        </div>
      ))}
      <AddBtn onClick={() => set([...list, { name: '' }])}>Add a function</AddBtn>
    </div>
  )
}

/** One call of a declared function: which, its arguments, its credential. */
function CallEditor({ value, onChange, formNo, label }: {
  value: WizardFunctionCall | undefined
  onChange: (v: WizardFunctionCall | undefined) => void
  formNo?: string
  label: string
}) {
  const ctx = useWizardDesign()
  const call: WizardFunctionCall = value || { name: '' }
  const patch = (p: Partial<WizardFunctionCall>) => {
    const next = { ...call, ...p } as Record<string, unknown>
    for (const k of Object.keys(p)) if (next[k] === undefined) delete next[k]
    onChange(next as unknown as WizardFunctionCall)
  }
  const names = (ctx.model.functions || []).map((f) => f.name).filter(Boolean)
  const cred = call.credential
  return (
    <div>
      <Field label={label}>
        <SelectInput value={call.name} options={names} empty={names.length ? '— pick a function —' : 'Declare one on the Base tab first'}
          disabled={ctx.readOnly} onChange={(name) => patch({ name })} />
      </Field>
      <Field label="Arguments (name → template, rendered on the server)">
        <KeyValueEditor value={call.input} onChange={(input) => patch({ input })} formNo={formNo}
          keyPlaceholder="argument" valuePlaceholder="{header.INV_NO}" />
      </Field>
      <Field label="Credential (a transaction marked as a credential store)">
        {!cred ? (
          <AddBtn onClick={() => patch({ credential: { object: '', match: {} } })}>Use a stored credential</AddBtn>
        ) : (
          <div>
            <ObjectPicker objType="T" value={cred.object}
              onChange={(object) => patch({ credential: { ...cred, object } })} />
            <KeyValueEditor value={cred.match} onChange={(match) => patch({ credential: { ...cred, match } })}
              formNo={formNo} keyPlaceholder="COLUMN" valuePlaceholder="{header.BRANCH}" addLabel="Add a context column" />
            <div style={hintStyle}>
              The most specific active row wins; a blank stored value matches anything. The secret is decrypted
              on the server for the function only — never sent to the browser.
            </div>
            <button type="button" style={{ marginTop: 4 }} onClick={() => patch({ credential: undefined })}>No credential</button>
          </div>
        )}
      </Field>
    </div>
  )
}

export function FunctionStepEditor({ step, onChange }: {
  step: WizardStep
  onChange: (p: Partial<WizardStep>) => void
}) {
  return (
    <div>
      <Sub title="Run on the server">
        <CallEditor value={step.function} onChange={(fn) => onChange({ function: fn })}
          formNo={step.form_no} label="Function" />
        <Grid2>
          <Field label="On error">
            <RouteStringPicker value={step.on_error} onChange={(on_error) => onChange({ on_error: on_error || undefined })} />
          </Field>
        </Grid2>
        <div style={hintStyle}>
          The function returns a dict: <code>error</code> / <code>errors</code> stop the step (a code routes On issue),
          <code> message</code> is shown, everything else is the answer mapped below.
        </div>
      </Sub>
      <PollEditor step={step} onChange={onChange} />
      <AnswerMapping step={step} onChange={onChange} title="Set from the answer (target → JSONPath)" />
    </div>
  )
}

/** A long job watched from the browser: the function returns
 *  `{poll: {url, headers?}, state?}`; the browser polls the status address
 *  until DONE, then the finish function fetches the real result once. */
function PollEditor({ step, onChange }: { step: WizardStep; onChange: (p: Partial<WizardStep>) => void }) {
  const poll = step.poll
  const set = (p: Partial<WizardPollBlock> | null) => onChange({ poll: p === null ? undefined : { ...(poll || { done: { path: '$.status', in: ['COMPLETED'] } }), ...p } as WizardPollBlock })
  const condText = (c?: WizardPollCondition) => (c?.in || []).map(String).join(', ')
  const condFrom = (path: string, list: string) => ({ path, in: list.split(',').map(x => x.trim()).filter(Boolean) })
  return (
    <Sub title="Long-running job (optional)">
      {!poll ? (
        <>
          <div style={hintStyle}>
            For a provider that answers “accepted — job 123” and finishes later: the function returns
            <code> {'{poll: {url}, state}'}</code>, the user’s browser watches the status address (no load on this
            server), and a finish function fetches the real result once.
          </div>
          <AddBtn onClick={() => set({})}>Watch the job from the browser</AddBtn>
        </>
      ) : (
        <>
          <Grid2>
            <Field label="Status path (JSONPath)">
              <TextInput mono value={poll.done?.path} onChange={(path) => set({ done: { ...poll.done, path } })} />
            </Field>
            <Field label="Done when it is one of">
              <TextInput value={condText(poll.done)} placeholder="COMPLETED, DONE"
                onChange={(v) => set({ done: condFrom(poll.done?.path || '$.status', v) })} />
            </Field>
            <Field label="Failed when it is one of">
              <TextInput value={condText(poll.fail)} placeholder="FAILED, REJECTED"
                onChange={(v) => set({ fail: v.trim() ? condFrom(poll.done?.path || '$.status', v) : undefined })} />
            </Field>
            <Field label="Check every (seconds)">
              <NumberInput value={poll.interval_s} min={2} placeholder="5" onChange={(interval_s) => set({ interval_s })} />
            </Field>
            <Field label="Give up after (minutes)">
              <NumberInput value={poll.timeout_min} min={1} placeholder="30" onChange={(timeout_min) => set({ timeout_min })} />
            </Field>
          </Grid2>
          <CallEditor value={poll.finish} onChange={(finish) => set({ finish })} formNo={step.form_no}
            label="Finish function — fetches the result (api.state()); none ⇒ the status answer is the result" />
          <button type="button" onClick={() => set(null)}>Not a long-running job</button>
        </>
      )}
    </Sub>
  )
}

export function RedirectStepEditor({ step, onChange }: {
  step: WizardStep
  onChange: (p: Partial<WizardStep>) => void
}) {
  const blk: WizardRedirectBlock = step.redirect || {}
  const patch = (p: Partial<WizardRedirectBlock>) => {
    const next = { ...blk, ...p } as Record<string, unknown>
    for (const k of Object.keys(p)) if (next[k] === undefined) delete next[k]
    onChange({ redirect: next as WizardRedirectBlock })
  }
  const how = blk.prepare ? 'prepare' : 'url'
  return (
    <div>
      <Sub title="Out to the other site">
        <Field label="The address">
          <SelectInput value={how}
            onChange={(v) => patch(v === 'prepare'
              ? { prepare: blk.prepare || { name: '' }, url: undefined }
              : { prepare: undefined, url: blk.url || 'https://' })}
            options={[
              { value: 'prepare', label: 'Built by a function (logs in, starts a session)' },
              { value: 'url', label: 'A fixed URL template' },
            ]} />
        </Field>
        {how === 'prepare' ? (
          <CallEditor value={blk.prepare} onChange={(prepare) => patch({ prepare })} formNo={step.form_no}
            label="Prepare function — returns {url, method?, form?, state?}" />
        ) : (
          <Field label="URL — put {run.callback_url} where the other site is told to come back">
            <TextInput mono value={blk.url} onChange={(url) => patch({ url })}
              placeholder="https://pay.example.com/start?return={run.callback_url}" />
          </Field>
        )}
        <Grid2>
          <Field label="Button caption">
            <TextInput value={blk.button} placeholder="Open the supplier's shop"
              onChange={(button) => patch({ button: button || undefined })} />
          </Field>
          <Field label="Wait at most (minutes)">
            <NumberInput value={blk.timeout_min} min={1} placeholder="30"
              onChange={(timeout_min) => patch({ timeout_min })} />
          </Field>
          <Field label="On error">
            <RouteStringPicker value={step.on_error} onChange={(on_error) => onChange({ on_error: on_error || undefined })} />
          </Field>
        </Grid2>
      </Sub>
      <Sub title="Back from the other site">
        {blk.receive ? (
          <>
            <CallEditor value={blk.receive} onChange={(receive) => patch({ receive })} formNo={step.form_no}
              label="Receive function — reads api.payload(), returns the answer" />
            <button type="button" onClick={() => patch({ receive: undefined })}>No receive function</button>
          </>
        ) : (
          <>
            <div style={hintStyle}>
              Without a receive function the posted JSON (or form fields) is the answer. Add one to check a
              signature, parse XML (cXML / OCI) or match items.
            </div>
            <AddBtn onClick={() => patch({ receive: { name: '' } })}>Add a receive function</AddBtn>
          </>
        )}
      </Sub>
      <AnswerMapping step={step} onChange={onChange} title="Set from the answer (target → JSONPath)" />
    </div>
  )
}
